import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Modal } from '@/components/Modal';
import { TaskModalHeader } from '@/components/TaskModal/TaskModalHeader';
import { User, Flag, Clock, Calendar, Tag, Plus, X, AlertCircle, Link2, Check, Maximize2, Minimize2, MoreHorizontal, Search, ChevronDown, ChevronLeft, Loader2, GitBranch, GitCommit, Copy, RefreshCw } from 'lucide-react';
import { TaskDescription } from '@/components/TaskModal/TaskDescription';
import { ActivitySection } from '@/components/TaskModal/ActivitySection';
// DetailsSidebar intentionally not used in Subtask modal per requirement
import type { Subtask, Task, TaskWorklog, UpdateSubtaskData } from '@/types';
import { commentAPI } from '@/api/commentAPI';
import { deleteComment as apiDeleteComment, updateComment as apiUpdateComment } from '@/utils/commentUtils';
import { mastersAPI, WorkflowStatus, Priority } from '@/api/mastersApi';
import EditableEstimateField from '@/components/TaskModal/EditableEstimateField';
import { LogWorkModal } from '@/components/TaskModal/LogWorkModal';
import { formatMinutes, formatDateTimeShort } from '@/utils/timeTracking';
import toast from 'react-hot-toast';
import { Comment as ProjectComment } from '@/types/comments';
import AvatarTooltip from '@/components/AvatarTooltip';
import { useAppSelector } from '@/store/hooks';
import { ensureHtml, toPlainText } from '@/utils/richText';
import { subtaskAPI } from '@/api/subtaskAPI';
import { wordDocApi } from '@/api/wordDocApi';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { PriorityIcon } from '@/components/PriorityIcon';
import { useNavigate } from 'react-router-dom';
import SidebarDetailItem from '@/components/TaskModal/SidebarDetailItem';
import { FileUpload } from '@/components/TaskModal/FileUpload';

interface SubtaskModalProps {
  subtask: Subtask;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: UpdateSubtaskData) => Promise<void>;
  onDelete?: () => Promise<void>;
  parentKey?: string; // e.g., AHM-123
  parentTitle?: string;
  parentTask?: Task;
  onOpenParent?: (parentTask: Task) => void;
  projectMembers?: any[];
  activity?: any[];
  projectKey?: string;
  project?: any
  onAddComment?: (taskId: string, html: string, parentId?: string, plainText?: string) => Promise<ProjectComment>;
  onUpdateComment?: (commentId: string, html: string, plainText?: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  onSubtaskUpdated?: (updatedSubtask: Subtask) => void; // Callback when subtask is updated
  isViewerOnly?: boolean;
}

const SubtaskModal: React.FC<SubtaskModalProps> = ({ subtask, isOpen, onClose, onSave, /* onDelete */ parentKey, /* parentTitle */ parentTask, onOpenParent, projectMembers = [], activity = [], projectKey, project, onAddComment, onUpdateComment, onDeleteComment, onSubtaskUpdated, isViewerOnly}) => {
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [title, setTitle] = useState(subtask.title);
  const [description, setDescription] = useState(subtask.description || '');
  const [localWorkflowId, setLocalWorkflowId] = useState<string | undefined>(String((subtask as any)?.workflow_status_id || ''));
  // Track completion state if needed later (currently derived from subtask)
  // const [isCompleted, setIsCompleted] = useState(!!subtask.is_completed);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [selectedPriorityId, setSelectedPriorityId] = useState<string | undefined>(String((subtask as any)?.priority_id || ''));
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const priorityRef = useRef<HTMLDivElement | null>(null);
  const [isPriorityUpdating, setIsPriorityUpdating] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | undefined>(String((subtask as any)?.assigned_to || ''));
  const [selectedReporterId, setSelectedReporterId] = useState<string | undefined>(String(parentTask?.reporter_id || ''));
  const [comments, setComments] = useState<ProjectComment[]>([]);
  const [activityItems, setActivityItems] = useState<any[]>([]);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState<boolean>(false);
  const [assigneeSearch, setAssigneeSearch] = useState<string>('');
  const assigneeRef = useRef<HTMLDivElement | null>(null);
  const [showReporterDropdown, setShowReporterDropdown] = useState<boolean>(false);
  const reporterRef = useRef<HTMLDivElement | null>(null);
  const currentUserId = currentUser?.id ? String(currentUser.id) : undefined;
  const currentUserName = currentUser?.name || currentUser?.display_name || [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ').trim() || currentUser?.username || currentUser?.email || 'You';
  const currentUserInitials = (() => {
    const parts = currentUserName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  })();
  const [currentProjectMembers, setCurrentProjectMembers] = useState<any[]>([]);
  const [isAssigneeUpdating, setIsAssigneeUpdating] = useState(false);


  // Labels state (even if API doesn't support, make UI functional)
  const [labels, setLabels] = useState<string[]>(() => {
    const raw = (subtask as any)?.labels;
    return Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',').map(l => l.trim()).filter(Boolean) : []);
  });
  const [isLabelEditing, setIsLabelEditing] = useState(false);
  const [labelInput, setLabelInput] = useState('');
  const [labelLoading, setLabelLoading] = useState(false);

  // Due date state
  const [dueDateValue, setDueDateValue] = useState<string>(() => {
    const raw = (subtask as any)?.due_date || '';
    return raw ? String(raw).substring(0, 10) : '';
  });
  const [isDueDateEditing, setIsDueDateEditing] = useState(false);
  const [dueDateLoading, setDueDateLoading] = useState(false);

  // Local subtask state to track updates
  const [localSubtask, setLocalSubtask] = useState<Subtask>(subtask);
  // Time tracking state
  const [worklogs, setWorklogs] = useState<TaskWorklog[]>([]);
  const [isWorklogsLoading, setIsWorklogsLoading] = useState(false);
  const [isLogWorkOpen, setIsLogWorkOpen] = useState(false);
  const [isLoggingWork, setIsLoggingWork] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [localTimeSpent, setLocalTimeSpent] = useState<number>(0);
  const [localRemaining, setLocalRemaining] = useState<number | undefined>(undefined);
  const [showWorklogs, setShowWorklogs] = useState(false);
  const [showDevelopmentDropdown, setShowDevelopmentDropdown] = useState(true);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [showCommitDropdown, setShowCommitDropdown] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const layoutContainerRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [sidebarWidthPct, setSidebarWidthPct] = useState<number>(30);
  const [hasDragged, setHasDragged] = useState(false);
  const dragStateRef = useRef<{
    startX: number;
    startPct: number;
  } | null>(null);
  const developmentRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const taskId = `${projectKey ? projectKey : 'AHM'}-${(parentTask as any).display_number ?? parentTask?.id?.substring(0, 6)}`;
  const subtaskKey = useMemo(() => {
    const parent = parentKey || taskId;
    const subDisplay = (subtask as any)?.display_number || (subtask as any)?.code;
    const fallback = String(subtask.id).slice(-4).toUpperCase();
    return subDisplay ? `${parent}.${subDisplay}` : `${parent}.${fallback}`;
  }, [parentKey, taskId, (subtask as any)?.display_number, (subtask as any)?.code, subtask.id]);

  const generateBranchName = () => {
    const cleanTitle = (subtask.title || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return `${subtaskKey}-${cleanTitle || 'subtask'}`;
  };

  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(successMessage);
    }
  };


  // Update local time tracking state when subtask changes
  useEffect(() => {
    setLocalTimeSpent((subtask as any)?.time_spent_minutes || 0);
    setLocalRemaining(
      typeof (subtask as any)?.remaining_time_minutes === "number" ? (subtask as any).remaining_time_minutes : undefined
    );
  }, [(subtask as any)?.time_spent_minutes, (subtask as any)?.remaining_time_minutes, subtask.id]);

  // Load worklogs when modal opens
  const loadWorklogs = useCallback(async () => {
    if (!subtask.id) return;
    setIsWorklogsLoading(true);
    try {
      const logs = await subtaskAPI.getWorklogs(subtask.id);
      setWorklogs(logs);
    } catch (err) {
      console.error("Failed to load worklogs:", err);
      setWorklogs([]);
    } finally {
      setIsWorklogsLoading(false);
    }
  }, [subtask.id]);

  useEffect(() => {
    if (isOpen && subtask.id) {
      loadWorklogs();
    }
  }, [isOpen, subtask.id, loadWorklogs]);

  // Handle worklog submission
  const handleLogWorkSubmit = useCallback(async (payload: {
    time_spent_minutes: number;
    started_at?: string;
    comment?: string;
    adjust_remaining: "auto" | "set" | "none";
    remaining_time_minutes?: number | null;
  }) => {
    if (!subtask.id) return;
    setIsLoggingWork(true);
    try {
      const response = await subtaskAPI.addWorklog(subtask.id, payload);
      setWorklogs((prev) => [
        response.worklog,
        ...prev.filter((log) => log.id !== response.worklog.id),
      ]);
      setLocalTimeSpent((response.subtask as any)?.time_spent_minutes || 0);
      setLocalRemaining(
        typeof (response.subtask as any)?.remaining_time_minutes === "number"
          ? (response.subtask as any).remaining_time_minutes
          : undefined
      );
      // Update local subtask state
      setLocalSubtask(prev => ({ ...prev, ...response.subtask } as any));
      // Trigger parent update
      await onSave(response.subtask);
      toast.success("Work logged");
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message;
      toast.error(detail || "Failed to log work");
      throw error;
    } finally {
      setIsLoggingWork(false);
    }
  }, [subtask.id, onSave]);

  // Update local subtask when prop changes
  useEffect(() => {
    setLocalSubtask(subtask);
  }, [subtask]);

  useEffect(() => {
    (async () => {
      try {
        const [ws, prios] = await Promise.all([
          mastersAPI.getWorkflowStatuses(),
          mastersAPI.getPriorities()
        ]);
        setWorkflowStatuses((ws || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
        setPriorities(prios || []);
      } catch {
        setWorkflowStatuses([]);
        setPriorities([]);
      }
    })();
  }, []);

  // Close assignee dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!showAssigneeDropdown) return;
      if (assigneeRef.current && !assigneeRef.current.contains(e.target as Node)) {
        setShowAssigneeDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showAssigneeDropdown]);

  // Close reporter dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!showReporterDropdown) return;
      if (reporterRef.current && !reporterRef.current.contains(e.target as Node)) {
        setShowReporterDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showReporterDropdown]);

  // Close more menu dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
      if (priorityRef.current && !priorityRef.current.contains(event.target as Node)) {
        setShowPriorityDropdown(false);
      }
      if (developmentRef.current && !developmentRef.current.contains(event.target as Node)) {
        setShowBranchDropdown(false);
        setShowCommitDropdown(false);
      }
    };
    if (showMoreMenu || showPriorityDropdown || showBranchDropdown || showCommitDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu, showPriorityDropdown, showBranchDropdown, showCommitDropdown]);

  // Sidebar drag handlers
  const handleSidebarDragMove = (e: MouseEvent) => {
    if (!dragStateRef.current || !layoutContainerRef.current) return;
    const containerRect = layoutContainerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width || 1;
    const deltaX = dragStateRef.current.startX - e.clientX;
    const deltaPct = (deltaX / containerWidth) * 100;
    let nextPct = dragStateRef.current.startPct + deltaPct;

    const minPx = 260;
    const maxPct = 70;
    const minPct = (minPx / containerWidth) * 100;

    if (Number.isFinite(minPct)) {
      nextPct = Math.max(minPct, nextPct);
    }
    nextPct = Math.min(maxPct, nextPct);

    setSidebarWidthPct(nextPct);
  };

  const handleSidebarDragEnd = () => {
    dragStateRef.current = null;
    document.removeEventListener('mousemove', handleSidebarDragMove);
    document.removeEventListener('mouseup', handleSidebarDragEnd);
  };

  const startSidebarDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!layoutContainerRef.current) return;
    e.preventDefault();

    let initialPct = sidebarWidthPct;

    // On first drag, derive initial width from existing layout (max-w-md etc.)
    if (!hasDragged && sidebarRef.current) {
      const containerRect = layoutContainerRef.current.getBoundingClientRect();
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width || 1;
      initialPct = (sidebarRect.width / containerWidth) * 100;
      setSidebarWidthPct(initialPct);
      setHasDragged(true);
    }

    dragStateRef.current = {
      startX: e.clientX,
      startPct: initialPct,
    };
    document.addEventListener('mousemove', handleSidebarDragMove);
    document.addEventListener('mouseup', handleSidebarDragEnd);
  };

  useEffect(() => {
    if (parentTask?.reporter_id) {
      setSelectedReporterId(String(parentTask.reporter_id));
    }
  }, [parentTask?.reporter_id]);

  useEffect(() => {
    const fetchProjectMembers = async () => {
      try {
        const data = await projectMemberAPI.getProjectMembers(parentTask?.project_id || "");
        setCurrentProjectMembers(data)
      } catch (error) {
        console.error('Failed to fetch project members:', error);
      }
    }
    fetchProjectMembers()
  }, []);

  const handleExportWord = async () => {
    try {
      const subtaskKey = parentKey
        ? `${parentKey}.${(subtask as any)?.display_number || ''}`
        : `SUBTASK-${subtask.id.slice(-6)}`;
      await wordDocApi.exportSubtaskWord(subtask.id, `${subtaskKey}.doc`);
      setShowMoreMenu(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export subtask');
    }
  };

  const colorFromSeed = (seed: string) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-red-500', 'bg-yellow-500', 'bg-cyan-500'];
    if (!seed) return 'bg-gray-400';
    const sum = Array.from(seed).reduce((a, c) => a + c.charCodeAt(0), 0);
    return colors[sum % colors.length];
  };

  const currentAssignee = useMemo(() => {
    const m = (projectMembers || []).find((mm: any) => String(mm.user_id) === String(selectedAssigneeId));
    if (!m) return { name: 'Unassigned', initials: 'U', color: 'bg-gray-400', id: '' };
    const name = m.user_display_name || m.username || m.user_email || 'User';
    const parts = String(name).trim().split(/\s+/);
    const initials = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.substring(0, 1).toUpperCase();
    return { name, initials, color: colorFromSeed(m.user_id || name), id: String(m.user_id) };
  }, [projectMembers, selectedAssigneeId]);

  const assigneeOptions = useMemo(() => [
    { id: null as null | string, name: 'Unassigned', email: '', initials: '', color: 'bg-gray-400' },
    ...((projectMembers || []).map((m: any) => {
      const name = m.user_display_name || m.username || m.user_email || 'User';
      const parts = String(name).trim().split(/\s+/);
      const initials = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.substring(0, 1).toUpperCase();
      return { id: String(m.user_id), name, email: m.user_email || '', initials, color: colorFromSeed(m.user_id || name) };
    }))
  ], [projectMembers]);

  const filteredAssignees = assigneeOptions.filter(
    (option) =>
      option.name.toLowerCase().includes(assigneeSearch.toLowerCase()) ||
      option.email.toLowerCase().includes(assigneeSearch.toLowerCase())
  );
  const currentUserColor = colorFromSeed(currentUserId || currentUserName);

  // Helper function to save and refresh subtask
  const saveAndRefreshSubtask = async (updates: any) => {
    // Optimistically update local state
    setLocalSubtask(prev => ({ ...prev, ...updates } as any));
    try {
      // Save to backend
      await onSave(updates);
      // Fetch fresh data from API to ensure consistency
      try {
        const fresh = await subtaskAPI.get(subtask.id);
        setLocalSubtask(fresh);
        // Update relevant local state based on fresh data
        if (updates.priority_id !== undefined) {
          setSelectedPriorityId(String(fresh.priority_id || ''));
        }
        if (updates.assigned_to !== undefined) {
          setSelectedAssigneeId(String(fresh.assigned_to || ''));
        }
        if (updates.workflow_status_id !== undefined) {
          setLocalWorkflowId(String(fresh.workflow_status_id || ''));
        }
        if (updates.labels !== undefined) {
          const raw = (fresh as any)?.labels;
          setLabels(Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',').map(l => l.trim()).filter(Boolean) : []));
        }
        if (updates.due_date !== undefined) {
          const rawDue = (fresh as any)?.due_date || '';
          setDueDateValue(rawDue ? String(rawDue).substring(0, 10) : '');
        }
        // Notify parent of update
        if (onSubtaskUpdated) {
          onSubtaskUpdated(fresh);
        }
        return fresh;
      } catch (refreshErr) {
        console.error('Failed to refresh subtask:', refreshErr);
        return null;
      }
    } catch (saveErr) {
      // Revert on error
      setLocalSubtask(subtask);
      if (updates.priority_id !== undefined) {
        setSelectedPriorityId(String((subtask as any)?.priority_id || ''));
      }
      if (updates.assigned_to !== undefined) {
        setSelectedAssigneeId(String((subtask as any)?.assigned_to || ''));
      }
      if (updates.labels !== undefined) {
        const raw = (subtask as any)?.labels;
        setLabels(Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',').map(l => l.trim()).filter(Boolean) : []));
      }
      if (updates.due_date !== undefined) {
        const rawDue = (subtask as any)?.due_date || '';
        setDueDateValue(rawDue ? String(rawDue).substring(0, 10) : '');
      }
      throw saveErr;
    }
  };

  const handlePrioritySelect = async (priorityId?: string | null) => {
    const nextId = priorityId ?? undefined;
    if (String(nextId || '') === String(selectedPriorityId || '')) {
      setShowPriorityDropdown(false);
      return;
    }
    setIsPriorityUpdating(true);
    try {
      setSelectedPriorityId(nextId ? String(nextId) : undefined);
      await onSave({ priority_id: nextId } as any);
      setLocalSubtask((prev) => ({ ...prev, priority_id: nextId } as any));
      setShowPriorityDropdown(false);
      toast.success(nextId ? "Priority updated" : "Priority removed");
    } catch (error: any) {
      console.error("Failed to update priority", error);
      toast.error(error?.message || "Failed to update priority");
      setSelectedPriorityId(String((subtask as any)?.priority_id || ''));
    } finally {
      setIsPriorityUpdating(false);
    }
  };

  const isAssignedToCurrentUser = currentUserId
    ? String(selectedAssigneeId || '') === String(currentUserId)
    : false;

  const handleAssigneeSelect = async (assigneeId?: string | null) => {
    const nextId = assigneeId ?? undefined;
    if (String(nextId || '') === String(selectedAssigneeId || '')) {
      setShowAssigneeDropdown(false);
      return;
    }
    setIsAssigneeUpdating(true);
    try {
      setSelectedAssigneeId(nextId);
      setShowAssigneeDropdown(false);
      setAssigneeSearch('');
      await saveAndRefreshSubtask({ assigned_to: assigneeId || undefined } as any);
    } catch (error) {
      console.error('Failed to update assignee', error);
      toast.error('Failed to update assignee');
    } finally {
      setIsAssigneeUpdating(false);
    }
  };

  const currentReporter = useMemo(() => {
    const m = (currentProjectMembers || projectMembers || []).find((mm: any) => String(mm.user_id) === String(selectedReporterId));
    if (!m) return { name: 'Unassigned', initials: 'U', color: 'bg-gray-400', id: '' };
    const name = m.user_display_name || m.username || m.user_email || 'User';
    const parts = String(name).trim().split(/\s+/);
    const initials = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.substring(0, 1).toUpperCase();
    return { name, initials, color: colorFromSeed(m.user_id || name), id: String(m.user_id) };
  }, [currentProjectMembers, projectMembers, selectedReporterId]);


  // Keep local selection in sync when different subtasks are opened or properties change
  useEffect(() => {
    setSelectedPriorityId(String((subtask as any)?.priority_id || ''));
    setSelectedAssigneeId(String((subtask as any)?.assigned_to || ''));
    setLocalWorkflowId(String((subtask as any)?.workflow_status_id || ''));
    setLocalSubtask(subtask);

    // Update labels
    const raw = (subtask as any)?.labels;
    setLabels(Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',').map(l => l.trim()).filter(Boolean) : []));

    // Update due date
    const rawDue = (subtask as any)?.due_date || '';
    setDueDateValue(rawDue ? String(rawDue).substring(0, 10) : '');
  }, [subtask?.id, (subtask as any)?.priority_id, (subtask as any)?.assigned_to, (subtask as any)?.workflow_status_id, (subtask as any)?.labels, (subtask as any)?.due_date]);

  // Load comments for this subtask when modal opens or subtask id changes
  useEffect(() => {
    if (!subtask?.id) return;
    (async () => {
      try {
        const data = await commentAPI.getComments(subtask.id);
        setComments(data || []);
        setActivityItems(data || []); // reuse data as activity feed for now
      } catch {
        setComments([]);
        setActivityItems([]);
      }
    })();
  }, [subtask?.id]);

  // Local save flow is handled inline via onSave calls for each field update

  const taskLike: Task = useMemo(() => ({
    ...(parentTask as any),
    id: subtask.id as any,
    title: title as any,
    description: description as any,
    // Provide subtask-specific state so StatusDropdown shows correct value
    status: (subtask as any)?.status as any,
    workflow_status_id: (localWorkflowId as any) || ((subtask as any)?.workflow_status_id as any),
    assigned_to: (selectedAssigneeId as any) || ((subtask as any)?.assigned_to as any),
    priority_id: (selectedPriorityId as any) || ((subtask as any)?.priority_id as any),
    original_estimate_minutes: (localSubtask as any)?.original_estimate_minutes || (subtask as any)?.original_estimate_minutes,
    remaining_time_minutes: (localSubtask as any)?.remaining_time_minutes || (subtask as any)?.remaining_time_minutes,
    due_date: dueDateValue ? `${dueDateValue}T00:00:00` : (subtask as any)?.due_date,
    labels: labels,
  }), [parentTask, subtask.id, title, description, (subtask as any)?.status, localWorkflowId, selectedAssigneeId, selectedPriorityId, localSubtask, dueDateValue, labels]);

  const modalContainerSizing = isMaximized
    ? 'max-w-full w-full h-full m-2 flex flex-col'
    : 'max-w-[75vw] w-full h-[82vh] flex flex-col';

  const modalContentSizing = 'flex-1 overflow-hidden flex flex-col';

  const handleBack = useCallback(() => {
    if (parentTask && onOpenParent) {
      onOpenParent(parentTask);
    } else {
      onClose();
    }
  }, [onClose, parentTask, onOpenParent]);

  const handleClose = useCallback(() => {
    // Treat "Closing" the subtask as going back to the parent if one exists
    if (parentTask && onOpenParent) {
      onOpenParent(parentTask);
    } else {
      onClose();
    }
  }, [onClose, parentTask, onOpenParent]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      size="xl"
      hideHeader
      containerClassName={`rounded-xl transition-all ${modalContainerSizing}`}
      contentClassName={`pt-4 ${modalContentSizing}`}
    >
      {/* Top toolbar (Jira-like) */}
      <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
        <div className="flex items-center gap-4">
          <button 
            type="button"
            onClick={handleBack} 
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-blue-600 transition-colors shadow-sm"
          >
            <ChevronLeft size={16} />
            Back to Task
          </button>
          
          <div className="text-sm text-gray-600 flex items-center gap-2">
            {taskId && project && (
            <button
              className="text-blue-600 hover:underline"
              onClick={() => {
                onClose();
                navigate(`/projects/${project.id}/board`);
              }}
            >
              {project.name}
            </button>
          )}
          <span className="text-gray-400">›</span>
            {parentKey && (
              <button className="text-blue-600 hover:underline" onClick={handleBack}>{parentKey}</button>
            )}
            <span className="text-gray-400">›</span>
            <span className="text-gray-800 font-medium truncate" title={title}>{title || 'Subtask'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group inline-block">
            <button
              type="button"
              onClick={async () => {
                const url = `${window.location.origin}/subtask/${subtask.id}`;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Link copied to clipboard");
                } catch (err) {
                  const textArea = document.createElement("textarea");
                  textArea.value = url;
                  textArea.style.position = "fixed";
                  textArea.style.opacity = "0";
                  document.body.appendChild(textArea);
                  textArea.select();
                  try {
                    document.execCommand("copy");
                    toast.success("Link copied to clipboard");
                  } catch (fallbackErr) {
                    toast.error("Failed to copy link");
                  }
                  document.body.removeChild(textArea);
                }
              }}
              className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors px-2 py-1 rounded hover:bg-gray-100"
            >
              <Link2 className="w-4 h-4" />
            </button>

            {/* Tooltip */}
            <div className="absolute left-1/2 -translate-x-1/2 top-8
                  opacity-0 group-hover:opacity-100
                  transition bg-gray-900 text-white text-xs
                  py-1 px-2 rounded shadow-lg whitespace-nowrap
                  pointer-events-none">
              Copy shareable link
              <div className="absolute left-1/2 -translate-x-1/2 top-full
                    border-4 border-transparent border-b-gray-900"></div>
            </div>
          </div>

          <div className="relative" ref={moreMenuRef}>
            <button
              type="button"
              className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
              aria-label="More options"
              onClick={() => setShowMoreMenu(!showMoreMenu)}
            >
              <MoreHorizontal size={18} />
            </button>
            {showMoreMenu && (
              <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px]">
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  onClick={handleExportWord}
                >
                  Export Word
                </button>
              </div>
            )}
          </div>

          <button
            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
            aria-label={isMaximized ? "Restore" : "Expand"}
            onClick={() => setIsMaximized(!isMaximized)}
          >
            {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>

          <button className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded" aria-label="Close" onClick={handleClose}>✕</button>
        </div>
      </div>
      <div
        ref={layoutContainerRef}
        className={`flex flex-1 ${isMaximized ? 'min-h-0' : 'h-[65vh]'}`}
      >
        {/* Main content */}
        <div
          className="flex-1 pr-6 overflow-y-auto taskmodal-scroll"
          style={hasDragged ? {
            flexBasis: `${100 - sidebarWidthPct}%`,
            minWidth: '260px',
          } : undefined}
        >
          <TaskModalHeader
            isViewerOnly={isViewerOnly}
            task={taskLike}
            onStatusUpdate={async (statusId) => {
              const selected = workflowStatuses.find(ws => String(ws.id) === String(statusId));
              const payload: Partial<Subtask> = selected
                ? { status: selected.name || String(statusId), ...(selected?.id ? { workflow_status_id: String(selected.id) as any } : {}) }
                : { status: String(statusId) };
              await onSave(payload);
              setLocalWorkflowId(String(selected?.id || statusId));
              // Update local state
              setLocalSubtask(prev => ({ ...prev, ...payload } as any));
            }}
            commentsLength={(activity || []).length}
            projectKey={projectKey}
            onTitleUpdate={async (newTitle) => {
              setTitle(newTitle);
              await onSave({ title: newTitle });
              // Update local state
              setLocalSubtask(prev => ({ ...prev, title: newTitle } as any));
            }}
            workflowStatuses={workflowStatuses as any}
          />
          <TaskDescription
            disabled={isViewerOnly}
            task={taskLike}
            onDescriptionUpdate={async (desc) => {
              setDescription(desc);
              await onSave({ description: desc });
              // Update local state
              setLocalSubtask(prev => ({ ...prev, description: desc } as any));
            }}
          />
          <div className="mt-6">
            <h4 className="font-medium text-gray-900 mb-2">Attachments</h4>
            <FileUpload taskId={subtask.id} isViewerOnly={isViewerOnly}/>
          </div>
          <ActivitySection
            isViewerOnly={isViewerOnly}
            taskId={subtask.id}
            activity={activityItems as any}
            comments={comments as any}
            users={(projectMembers || []).map((m: any) => ({ id: String(m.user_id), name: m.user_display_name || m.username || m.user_email || 'User', email: m.user_email || '' }))}
            onAddComment={async (taskId, html, parentId, plainText) => {
              const contentHtml = html;
              const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
              let createdComment: ProjectComment;
              if (onAddComment) {
                createdComment = await onAddComment(taskId, contentHtml, parentId, contentText);
              } else {
                createdComment = await commentAPI.createComment({
                  task_id: taskId,
                  content: contentText,
                  content_html: contentHtml,
                  content_text: contentText,
                  parent_comment_id: parentId
                });
              }
              const data = await commentAPI.getComments(subtask.id);
              setComments(data || []);
              setActivityItems(data || []);
              return createdComment;
            }}
            onDeleteComment={async (commentId) => {
              if (onDeleteComment) {
                await onDeleteComment(commentId);
              } else {
                await apiDeleteComment(commentId);
              }
              const data = await commentAPI.getComments(subtask.id);
              setComments(data || []);
              setActivityItems(data || []);
            }}
            onUpdateComment={async (commentId, html, plainText) => {
              const contentHtml = html;
              const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
              if (onUpdateComment) {
                await onUpdateComment(commentId, contentHtml, contentText);
              } else {
                await apiUpdateComment(commentId, contentText);
              }
              const data = await commentAPI.getComments(subtask.id);
              setComments(data || []);
              setActivityItems(data || []);
            }}
          />
        </div>

        {/* Sidebar drag handle */}
        <div
          className="w-1 cursor-col-resize bg-transparent hover:bg-blue-200 transition-colors"
          onMouseDown={startSidebarDrag}
          aria-hidden="true"
        />

        {/* Sidebar */}
        <div
          ref={sidebarRef}
          className="w-full max-w-md flex flex-wrap px-4 overflow-y-auto taskmodal-scroll"
          style={hasDragged ? {
            flexBasis: `${sidebarWidthPct}%`,
            maxWidth: '70%',
            minWidth: '260px',
          } : undefined}
        >
          <div className="space-y-5 border rounded-md p-3 w-full">
            <h3 className="text-lg font-semibold text-gray-900">Details</h3>

            {/* Assignee */}
            <SidebarDetailItem icon={User} label="Assignee">
              <div className="relative flex gap-2 items-center flex-1" ref={assigneeRef}>
                <button
                  type="button"
                  onClick={() => setShowAssigneeDropdown((v) => !v)}
                  disabled={isAssigneeUpdating}
                  className="flex items-center gap-2 p-2 w-full text-left hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors disabled:opacity-50"
                >
                  <AvatarTooltip label={currentAssignee.name || 'Unassigned'}>
                    <div
                      className={`w-6 h-6 ${currentAssignee.color} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                    >
                      {currentAssignee.initials || <User className="w-3 h-3" />}
                    </div>
                  </AvatarTooltip>
                  <span className="text-sm text-gray-900 flex-1 truncate">
                    {isAssigneeUpdating ? 'Updating...' : currentAssignee.name}
                  </span>
                </button>

                {showAssigneeDropdown && !isViewerOnly && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search assignees..."
                          value={assigneeSearch}
                          onChange={(e) => setAssigneeSearch(e.target.value)}
                          className="w-full pl-8 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                    </div>
                    {currentUserId && (
                      <div className="border-b border-gray-100">
                        <button
                          type="button"
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors ${isAssignedToCurrentUser ? 'bg-blue-50' : ''}`}
                          disabled={isAssignedToCurrentUser || isAssigneeUpdating}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (currentUserId) handleAssigneeSelect(currentUserId);
                          }}
                        >
                          <AvatarTooltip label={currentUserName} position="bottom">
                            <div className={`w-6 h-6 ${currentUserColor} rounded-full flex items-center justify-center text-white text-xs font-medium`}>
                              {currentUserInitials}
                            </div>
                          </AvatarTooltip>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">Assign to Me</div>
                            <div className="text-xs text-gray-500 truncate">{currentUserName}</div>
                          </div>
                          {isAssignedToCurrentUser && <Check className="w-4 h-4 text-blue-600" />}
                        </button>
                      </div>
                    )}

                    <div className="max-h-48 overflow-y-auto taskmodal-scroll">
                      {filteredAssignees.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">No assignees found</div>
                      ) : (
                        filteredAssignees.map((option) => (
                          <button
                            key={option.id ?? 'unassigned'}
                            type="button"
                            disabled={isAssigneeUpdating}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleAssigneeSelect(option.id);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 ${String(selectedAssigneeId || '') === String(option.id || '') ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                          >
                            <AvatarTooltip label={option.name || 'Unassigned'} position="bottom">
                              <div
                                className={`w-6 h-6 ${option.color} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                              >
                                {option.initials || <User className="w-3 h-3" />}
                              </div>
                            </AvatarTooltip>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">{option.name}</div>
                              {option.email && <div className="text-xs text-gray-500 truncate">{option.email}</div>}
                            </div>
                            {String(selectedAssigneeId || '') === String(option.id || '') && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </SidebarDetailItem>

            {/* Labels */}
            <SidebarDetailItem icon={Tag} label="Labels">
              <div className="flex flex-wrap gap-1 p-1">
                {labels.map((label, idx) => (
                  <div key={idx} className="flex items-center px-3 py-1 gap-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <span>{label}</span>
                    {!isViewerOnly && (
                    <button
                      type="button"
                      className="flex items-center justify-between mt-0.5 text-gray-400 hover:text-red-500"
                      onClick={async () => {
                        const updated = labels.filter((l) => l !== label);
                        setLabelLoading(true);
                        try {
                          await onSave({ labels: updated } as any);
                          setLabels(updated);
                          setLocalSubtask(prev => ({ ...prev, labels: updated } as any));
                          toast.success("Label removed");
                        } catch (err: any) {
                          console.error("REMOVE FAILED", err);
                          toast.error("Failed to remove label");
                        } finally {
                          setLabelLoading(false);
                        }
                      }}
                      disabled={labelLoading}
                    >
                      <X className="w-3 h-3" />
                    </button>
                    )}
                  </div>
                ))}

                {labelLoading && (
                  <span className="inline-flex items-center gap-2 px-2 py-1 text-xs text-blue-700 bg-blue-50 rounded-full">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Updating labels...
                  </span>
                )}
                 {!isViewerOnly && (
                   isLabelEditing ? (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const newLabel = labelInput.trim();
                      if (!newLabel) return;

                      const updated = [...labels, newLabel];
                      setLabelLoading(true);
                      try {
                        await onSave({ labels: updated } as any);
                        setLabels(updated);
                        setLocalSubtask(prev => ({ ...prev, labels: updated } as any));
                        setLabelInput("");
                        setIsLabelEditing(false);
                        toast.success("Label added");
                      } catch (err: any) {
                        toast.error("Failed to add label");
                      } finally {
                        setLabelLoading(false);
                      }
                    }}
                    className="flex items-center"
                  >
                    <input
                      type="text"
                      value={labelInput}
                      onChange={(e) => setLabelInput(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                      placeholder="Add label"
                      autoFocus
                    />
                    <button type="submit" className="ml-1 text-blue-500 hover:text-blue-700">
                      <Plus className="w-4 h-4" />
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="flex items-center justify-between px-3 py-1 gap-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 ml-1"
                    onClick={() => setIsLabelEditing(true)}
                  >
                    <Plus className="w-3 h-3" />
                    <span>Add</span>
                  </button>
                )
                )}

                {labels.length === 0 && !isLabelEditing && <span className="text-sm text-gray-500 mt-0.5 ml-1">No labels</span>}
              </div>
            </SidebarDetailItem>

            {/* Priority */}
            <SidebarDetailItem icon={Flag} label="Priority">
              <div className="relative flex gap-2 items-center flex-1" ref={priorityRef}>
                <button
                  type="button"
                  onClick={() => setShowPriorityDropdown((v) => !v)}
                  disabled={isPriorityUpdating}
                  className="flex items-center gap-2 px-1 py-2 w-full text-left hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors disabled:opacity-50"
                >
                  {selectedPriorityId ? (
                    <>
                      <PriorityIcon priority={priorities.find((p) => String(p.id) === String(selectedPriorityId)) || null} size={18} />
                      <span className="text-sm text-gray-900 flex-1 truncate">
                        {priorities.find((p) => String(p.id) === String(selectedPriorityId))?.name || 'Not set'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500 flex-1 truncate">Select priority…</span>
                  )}
                  {/* <ChevronDown className="w-4 h-4 text-gray-400" /> */}
                </button>

                {showPriorityDropdown && !isViewerOnly && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="max-h-48 overflow-y-auto taskmodal-scroll">
                      <button
                        type="button"
                        disabled={isPriorityUpdating}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handlePrioritySelect(undefined);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 ${selectedPriorityId ? '' : 'bg-blue-50 border-l-2 border-l-blue-500'}`}
                      >
                        <span className="text-sm text-gray-900 flex-1">None</span>
                        {!selectedPriorityId && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                      </button>
                      {priorities.map((p) => {
                        const pid = String(p.id);
                        const isActive = String(selectedPriorityId || '') === pid;
                        return (
                          <button
                            key={pid}
                            type="button"
                            disabled={isPriorityUpdating}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handlePrioritySelect(pid);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 ${isActive ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                          >
                            <PriorityIcon priority={p} size={18} />
                            <span className="text-sm text-gray-900 flex-1 truncate">{p.name}</span>
                            {isActive && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </SidebarDetailItem>

            {/* Due date */}
            <SidebarDetailItem
              icon={Calendar}
              label="Due date"
              value={
                dueDateValue
                  ? (() => {
                    const d = new Date(dueDateValue);
                    if (isNaN(d.getTime())) return dueDateValue;
                    const day = String(d.getDate()).padStart(2, "0");
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    const year = d.getFullYear();
                    return `${day}-${month}-${year}`;
                  })()
                  : "Not set"
              }
            >
              <div
                className={(() => {
                  if (!dueDateValue) return "flex items-center gap-2 px-1 py-2 hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors";
                  const due = new Date(dueDateValue);
                  const today = new Date();
                  const diffDays = Math.floor(
                    (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
                  );

                  if (diffDays < 0)
                    return "flex items-center gap-2 px-2 py-2 mr-2 hover:bg-gray-50 rounded border border-red-500 hover:border-red-500 transition-colors";
                  if (diffDays <= 1)
                    return "flex items-center gap-2 px-2 py-2 mr-2 hover:bg-gray-50 rounded border border-orange-400 hover:border-orange-400 transition-colors";
                  return "flex items-center gap-2 px-2 py-2 mr-2 hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors";
                })()}
              >
                {isDueDateEditing ? (
                  <>
                    <input
                      type="date"
                      value={dueDateValue ? dueDateValue.slice(0, 10) : ""}
                      onChange={(e) => setDueDateValue(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                      disabled={dueDateLoading}
                    />
                    <button
                      type="button"
                      className="text-sm font-medium text-blue-500 hover:text-blue-700"
                      onClick={async () => {
                        if (!dueDateValue || !dueDateValue.trim()) {
                          toast.error("Due date cannot be empty");
                          return;
                        }
                        setDueDateLoading(true);
                        try {
                          const dateStr = `${dueDateValue}T00:00:00`;
                          await onSave({ due_date: dateStr } as any);
                          setLocalSubtask(prev => ({ ...prev, due_date: dateStr } as any));
                          setIsDueDateEditing(false);
                          toast.success("Due date updated");
                        } catch (err: any) {
                          console.error(err);
                          toast.error(err?.message || "Failed to update due date");
                        } finally {
                          setDueDateLoading(false);
                        }
                      }}
                      disabled={dueDateLoading}
                    >
                      <Check className="w-4 h-4 text-green-500" />
                    </button>
                    <button
                      type="button"
                      className="text-sm font-medium text-gray-400 hover:text-gray-700"
                      onClick={() => {
                        setIsDueDateEditing(false);
                        const raw = (localSubtask as any)?.due_date || (subtask as any)?.due_date || '';
                        setDueDateValue(raw ? String(raw).substring(0, 10) : '');
                      }}
                      disabled={dueDateLoading}
                    >
                      <X className="w-4 h-4 text-red-600" />
                    </button>
                  </>
                ) : (
                  <>
                    {(() => {
                      if (!dueDateValue) return null;
                      const due = new Date(dueDateValue);
                      const today = new Date();
                      const diffDays = Math.floor(
                        (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
                      );

                      if (diffDays < 0)
                        return (
                          <div className="flex items-center text-red-600 text-sm font-medium">
                            <AlertCircle className="w-4 h-4 mr-1" /> Overdue
                          </div>
                        );
                      if (diffDays <= 1)
                        return (
                          <div className="flex items-center text-orange-500 text-sm font-medium">
                            <Clock className="w-4 h-4 mr-1" /> Due soon
                          </div>
                        );
                      return (
                        <div className="flex items-center text-gray-600 text-sm font-medium">
                          On track
                        </div>
                      );
                    })()}

                    <span className="text-sm text-gray-900">
                      {dueDateValue
                        ? (() => {
                          const d = new Date(dueDateValue);
                          if (isNaN(d.getTime())) return dueDateValue;
                          const day = String(d.getDate()).padStart(2, "0");
                          const month = String(d.getMonth() + 1).padStart(2, "0");
                          const year = d.getFullYear();
                          return `${day}-${month}-${year}`;
                        })()
                        : "Not set"}
                    </span>
                    {!isViewerOnly && (
                    <button
                      type="button"
                      className="ml-auto text-blue-500 hover:text-blue-700 flex items-center gap-1"
                      onClick={() => setIsDueDateEditing(true)}
                    >
                      <Calendar className="w-4 h-4" />
                    </button>
                    )}
                  </>
                )}
              </div>
            </SidebarDetailItem>

            {/* Reporter */}
            <SidebarDetailItem icon={User} label="Reporter" value={currentReporter.name}>
              <div className="flex items-center gap-2 p-2 w-full text-left rounded border border-transparent">
                <AvatarTooltip label={currentReporter.name || 'Unassigned'}>
                  <div
                    className={`w-6 h-6 ${currentReporter.color} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                  >
                    {currentReporter.initials}
                  </div>
                </AvatarTooltip>
                <span className="text-sm text-gray-900 truncate flex-1">{currentReporter.name}</span>
              </div>
            </SidebarDetailItem>

            {/* Time Tracking */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Time Tracking</h4>
                {!isViewerOnly && (
                <button
                  type="button"
                  onClick={() => setIsLogWorkOpen(true)}
                  className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-100 rounded"
                >
                  <Plus className="w-4 h-4" />
                  Log work
                </button>
                )}
              </div>

              {(() => {
                const original = (localSubtask as any)?.original_estimate_minutes ?? (subtask as any)?.original_estimate_minutes ?? 0;
                const spent = localTimeSpent;
                const remaining = localRemaining ?? (localSubtask as any)?.remaining_time_minutes ?? (subtask as any)?.remaining_time_minutes ?? 0;
                const progressPercent = original > 0 ? Math.min(100, Math.round((spent / original) * 100)) : undefined;

                if (original > 0 && progressPercent !== undefined) {
                  return (
                    <div className="mb-4 bg-gray-50 border border-gray-200 rounded p-3">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>{formatMinutes(spent)} logged</span>
                        <span>{formatMinutes(original)} original</span>
                      </div>
                      <div className="w-full h-2 bg-white rounded overflow-hidden border border-gray-200">
                        <div
                          className="h-full bg-blue-500"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatMinutes(remaining)} remaining · {progressPercent}% logged
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <SidebarDetailItem icon={Clock} label="Original Estimate">
                <EditableEstimateField
                  value={(localSubtask as any)?.original_estimate_minutes || (subtask as any)?.original_estimate_minutes || 0}
                  label="Original Estimate"
                  field="original_estimate_minutes"
                  taskId={subtask.id}
                  isSubtask={true}
                  onUpdateFields={async (_taskId, updates) => {
                    await onSave(updates as any);
                    setLocalSubtask(prev => ({ ...prev, ...updates } as any));  
                  }}
                  isviewerOnly={isViewerOnly}
                />
              </SidebarDetailItem>

              <SidebarDetailItem icon={Clock} label="Time Spent" value={formatMinutes(localTimeSpent)}>
                <div className="p-2">{formatMinutes(localTimeSpent)}</div>
              </SidebarDetailItem>

              <SidebarDetailItem icon={Clock} label="Time Remaining">
                <EditableEstimateField
                  value={localRemaining ?? (localSubtask as any)?.remaining_time_minutes ?? (subtask as any)?.remaining_time_minutes ?? 0}
                  label="Time Remaining"
                  field="remaining_time_minutes"
                  taskId={subtask.id}
                  isSubtask={true}
                  onUpdateFields={async (_taskId, updates) => {
                    await onSave(updates as any);
                    setLocalSubtask(prev => ({ ...prev, ...updates } as any));
                    setLocalRemaining((updates as any)?.remaining_time_minutes);
                  }}
                  isviewerOnly={isViewerOnly}
                />
              </SidebarDetailItem>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-medium text-gray-900">Worklogs</h5>
                  {!showWorklogs ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowWorklogs(true);
                        loadWorklogs();
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                      disabled={isWorklogsLoading}
                    >
                      Show log
                    </button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={loadWorklogs}
                        className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        disabled={isWorklogsLoading}
                        title="Refresh worklogs"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowWorklogs(false)}
                        className="p-1 text-gray-500 hover:text-gray-700"
                        title="Close worklogs"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                {showWorklogs && (
                  <>
                    {isWorklogsLoading ? (
                      <div className="text-sm text-gray-500">Loading worklogs…</div>
                    ) : worklogs.length === 0 ? (
                      <div className="text-sm text-gray-500">No work logged yet.</div>
                    ) : (
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1 taskmodal-scroll">
                        {worklogs.map((log) => (
                          <div key={log.id} className="border border-gray-200 rounded p-2 bg-white shadow-sm">
                            <div className="flex items-center justify-between text-sm font-medium text-gray-900">
                              <span>{formatMinutes(log.time_spent_minutes)}</span>
                              <span className="text-xs text-gray-500">{formatDateTimeShort(log.started_at)}</span>
                            </div>
                            <div className="text-xs text-gray-600">
                              {log.user_display_name || "User"}
                              {typeof log.remaining_time_minutes_after === "number" && (
                                <span className="text-gray-400"> · Remaining {formatMinutes(log.remaining_time_minutes_after)}</span>
                              )}
                            </div>
                            {log.comment && (
                              <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{log.comment}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="pt-4 border-t border-gray-200" ref={developmentRef}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Development</h4>

                  <button
                    onClick={() => setShowDevelopmentDropdown(!showDevelopmentDropdown)}
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${showDevelopmentDropdown ? "rotate-180" : ""
                        }`}
                    />
                  </button>
                </div>

                {showDevelopmentDropdown && (
                  <div className="mt-2 space-y-2 relative">
                    <div className="relative w-full">
                      <button
                        onClick={() => {
                          setShowBranchDropdown(!showBranchDropdown);
                          setShowCommitDropdown(false);
                        }}
                        className="w-full flex items-center justify-between px-0 pr-3 py-2 text-blue-600 hover:bg-gray-100 rounded transition-colors hover:underline"
                      >
                        <div className="flex items-center gap-2">
                          <GitBranch className="w-4 h-4" />
                          <span className="font-medium">Create branch</span>
                        </div>

                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${showBranchDropdown ? "rotate-180" : ""
                            }`}
                        />
                      </button>

                      {showBranchDropdown && (
                        <div className="absolute right-0 w-[260px] max-w-full overflow-hidden top-full mt-1 mr-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
                          <div className="text-xs font-semibold text-gray-700 mb-2">
                            GIT CREATE & CHECKOUT A NEW BRANCH
                          </div>

                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white border border-gray-500 rounded p-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                              <code className="text-sm text-gray-700 font-mono">
                                git checkout -b {generateBranchName()}
                              </code>
                            </div>

                            <button
                              onClick={() => {
                                copyToClipboard(
                                  `git checkout -b ${generateBranchName()}`,
                                  "Git command copied to clipboard!"
                                );
                                setShowBranchDropdown(false);
                                setShowDevelopmentDropdown(false);
                              }}
                              className="p-2 bg-gray-200 rounded text-gray-600 hover:bg-gray-300 transition-colors"
                              title="Copy command"
                            >
                              <Copy className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="relative w-full">
                      <button
                        onClick={() => {
                          setShowCommitDropdown(!showCommitDropdown);
                          setShowBranchDropdown(false);
                        }}
                        className="w-full flex items-center justify-between px-0 pr-3 py-2 text-blue-600 hover:bg-gray-100 rounded transition-colors hover:underline mt-1"
                      >
                        <div className="flex items-center gap-2">
                          <GitCommit className="w-4 h-4" />
                          <span className="font-medium">Create commit</span>
                        </div>

                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${showCommitDropdown ? "rotate-180" : ""
                            }`}
                        />
                      </button>

                      {showCommitDropdown && (
                        <div className="absolute right-0 w-[260px] max-w-full overflow-hidden top-full mb-1 mr-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
                          <div className="text-xs text-gray-700 mb-3">
                            Include keys in your commit messages to link them to your tracker items.
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Copy key
                              </label>

                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-white border border-gray-500 rounded p-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                                  <code className="text-sm text-gray-700 font-mono">
                                    {subtaskKey}
                                  </code>
                                </div>

                                <button
                                  onClick={() => {
                                    copyToClipboard(subtaskKey, "Subtask key copied to clipboard!");
                                    setShowCommitDropdown(false);
                                    setShowDevelopmentDropdown(false);
                                  }}
                                  className="p-2 bg-gray-200 rounded text-gray-600 hover:bg-gray-300 transition-colors"
                                  title="Copy subtask key"
                                >
                                  <Copy className="w-5 h-5" />
                                </button>
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Copy sample Git commit
                              </label>

                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-white border border-gray-500 rounded p-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                                  <code className="text-sm text-gray-700 font-mono">
                                    git commit -m "{subtaskKey} &lt;message&gt;"
                                  </code>
                                </div>

                                <button
                                  onClick={() => {
                                    copyToClipboard(
                                      `git commit -m "${subtaskKey} <message>"`,
                                      "Git commit template copied to clipboard!"
                                    );
                                    setShowCommitDropdown(false);
                                    setShowDevelopmentDropdown(false);
                                  }}
                                  className="p-2 bg-gray-200 rounded text-gray-600 hover:bg-gray-300 transition-colors"
                                  title="Copy commit template"
                                >
                                  <Copy className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Log Work Modal */}
      <LogWorkModal
        isOpen={isLogWorkOpen}
        onClose={() => setIsLogWorkOpen(false)}
        onSubmit={handleLogWorkSubmit}
        task={taskLike}
        isSubmitting={isLoggingWork}
      />
    </Modal>
  );
};

export default SubtaskModal;